export const RINK_TEMPLATES = [
  "full",
  "half",
  "third",
  "zone",
  "blank",
] as const;
export type RinkTemplate = (typeof RINK_TEMPLATES)[number];
export type RinkSide = "left" | "right";

export const AGE_GROUPS = [
  "U6",
  "U8",
  "U10",
  "U12",
  "U14",
  "U16",
  "U18",
  "Adult",
] as const;
export type AgeGroup = (typeof AGE_GROUPS)[number];

export const ELEMENT_COLORS = [
  { name: "Navy", value: "#102032" },
  { name: "Blue", value: "#1976b9" },
  { name: "Red", value: "#df3344" },
  { name: "Green", value: "#28a745" },
  { name: "Orange", value: "#ee7b21" },
  { name: "Black", value: "#111111" },
  { name: "White", value: "#ffffff" },
] as const;

export type Point = { x: number; y: number };
export type MarkerKind =
  | "player"
  | "defender"
  | "goalie"
  | "coach"
  | "puck"
  | "puckCluster"
  | "cone"
  | "goal"
  | "tire"
  | "barrier"
  | "stick"
  | "stickTires"
  | "dummy"
  | "square"
  | "cross";
export type PathKind =
  | "straight"
  | "skate"
  | "skatePuck"
  | "backward"
  | "backwardPuck"
  | "lateral"
  | "pass"
  | "shot"
  | "dropPass"
  | "pressure"
  | "stop"
  | "direction"
  | "pivot"
  | "free";
export type PathEnding =
  "arrow" | "shot" | "stop" | "direction" | "pivot" | "none";
export type MarkerNumber = 1 | 2 | 3 | 4 | 5;

export type MarkerElement = {
  id: string;
  type: "marker";
  kind: MarkerKind;
  point: Point;
  color: string;
  size: number;
  rotation?: number;
  length?: number;
  label?: string;
  number?: MarkerNumber;
  outlined?: boolean;
  tireStyle?: "ring" | "solid";
};

export type PathElement = {
  id: string;
  type: "path";
  kind: PathKind;
  points: Point[];
  color: string;
  width: number;
  ending?: PathEnding;
};

export type TextElement = {
  id: string;
  type: "text";
  point: Point;
  value: string;
  color: string;
  fontSize: number;
};
export type DiagramElement = MarkerElement | PathElement | TextElement;

export type Drill = {
  schemaVersion: 2;
  id: string;
  title: string;
  durationMinutes: number;
  ageGroups: AgeGroup[];
  objective: string;
  instructions: string;
  coachingPoints: string;
  rink: RinkTemplate;
  rinkSide: RinkSide;
  elements: DiagramElement[];
  source: {
    kind: "puckly-original" | "licensed" | "adapted" | "external-reference";
    url?: string;
  };
  updatedAt: string;
};

export const createDrill = (): Drill => ({
  schemaVersion: 2,
  id: crypto.randomUUID(),
  title: "Untitled drill",
  durationMinutes: 10,
  ageGroups: ["U10"],
  objective: "",
  instructions: "",
  coachingPoints: "",
  rink: "full",
  rinkSide: "left",
  elements: [],
  source: { kind: "puckly-original" },
  updatedAt: new Date().toISOString(),
});

export const elementId = () => crypto.randomUUID();

type LegacyDrill = Omit<Drill, "schemaVersion" | "rinkSide" | "elements"> & {
  schemaVersion: 1;
  elements: Array<
    | DiagramElement
    | (Omit<MarkerElement, "size"> & { color: string })
    | Omit<PathElement, "color" | "width">
    | Omit<TextElement, "color" | "fontSize">
  >;
};

export function normalizeDrill(value: Drill | LegacyDrill): Drill {
  return {
    ...value,
    schemaVersion: 2,
    rinkSide: "rinkSide" in value ? value.rinkSide : "left",
    elements: value.elements.map((element) => {
      if (element.type === "marker") {
        const legacyColor = element.color;
        return {
          size: 1,
          rotation: 0,
          ...element,
          color: legacyColor.startsWith("#")
            ? legacyColor
            : legacyColor === "red"
              ? "#df3344"
              : legacyColor === "blue"
                ? "#1976b9"
                : "#102032",
        } as MarkerElement;
      }
      if (element.type === "path")
        return {
          color:
            element.kind === "shot"
              ? "#df3344"
              : element.kind === "pass"
                ? "#1976b9"
                : "#111111",
          width: 1,
          ...element,
        } as PathElement;
      return { color: "#102032", fontSize: 22, ...element } as TextElement;
    }),
  };
}
